/**
 * Environment utility functions for determining correct URLs
 * based on the current environment (dev/staging/production)
 */

/**
 * Get the Gatherly Connect app base URL based on the current environment
 * @returns Base URL for Gatherly Connect app (without trailing slash)
 */
export const getGatherlyConnectBaseUrl = (): string => {
  const env = process.env.REACT_APP_ENV;
  
  // Production uses app.joinfabrik.com
  if (env === 'production') {
    return 'https://app.joinfabrik.com';
  }
  
  // Dev and staging use staging-app.joinfabrik.com
  return 'https://staging-app.joinfabrik.com';
};

/**
 * Get the full URL for a gathering's manage page
 * @param gatheringId - The ID of the gathering
 * @returns Full URL to the gathering's manage page
 */
export const getGatheringManageUrl = (gatheringId: string): string => {
  const baseUrl = getGatherlyConnectBaseUrl();
  return `${baseUrl}/manage/${gatheringId}`;
};

/**
 * Get the full URL for a gathering's check-in page
 * @param gatheringId - The ID of the gathering
 * @returns Full URL to the gathering's check-in page
 */
export const getGatheringCheckInUrl = (gatheringId: string): string => {
  const baseUrl = getGatherlyConnectBaseUrl();
  return `${baseUrl}/manage/${gatheringId}/check-in`;
};

/**
 * Get the CDN base URL that S3 object keys are served from.
 *
 * This mirrors the backend's `AWS_S3_BASE_URL` (api/schemas/base.py
 * `get_object_url`, which returns `<base>/<object_key>`). The API only ever
 * hands us a resolved `image_url` for a key that is already live, so anything
 * we need to render from a bare object key — a host's *proposed* gathering
 * image, for instance — has to be composed against this base.
 *
 * Hardcoded per environment rather than read from a new `REACT_APP_*` variable
 * for the same reason `getGatherlyConnectBaseUrl` above is: an unset variable
 * fails silently at runtime, whereas a constant that ships with the build is
 * always present and is reviewable in the diff. Callers must still prefer a
 * base recovered from a live `image_url`/`image_object_key` pair when they have
 * one, and must handle the image failing to load, so a drifted CDN domain
 * degrades to an explicit "cannot preview" rather than a broken <img>.
 *
 * @returns CDN base URL for S3-backed assets (without trailing slash)
 */
export const getAssetBaseUrl = (): string => {
  const env = process.env.REACT_APP_ENV;

  if (env === 'production') {
    return 'https://cdn.joinfabrik.com';
  }

  // Development and staging both point at the dev API, and therefore its CDN.
  return 'https://cdn-dev.joinfabrik.com';
};
